import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

interface PartitionCategory {
  label: string;
  percentage: number;
  color: string;
}

interface Category {
  _id: string;
  title: string;
  items: string[];
  color: string;
}

interface ApiResponse {
  heading: string;
  categories: Category[];
  partioncategory: PartitionCategory[];
}

interface ApiResponseWrapper {
  status: string;
  message: ApiResponse[];
}

const ChartGraph: React.FC = () => {
  const [chartTitle, setChartTitle] = useState<string>("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [partitionCategory, setPartitionCategory] = useState<PartitionCategory[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get<ApiResponseWrapper>(
          `${process.env.REACT_APP_LOCAL_URL}/api/sector-of-operation`
        );

        const data = response.data.message[0];
        setChartTitle(data.heading);
        setCategories(data.categories);
        setPartitionCategory(data.partioncategory);
      } catch (error) {
        console.error("Error fetching charts:", error);
      }
    };

    fetchData();
  }, []);

  // Memoized chart data
  const chartData = useMemo(() => {
    if (!partitionCategory) return null;

    return {
      labels: partitionCategory.map((p) => p.label),
      datasets: [
        {
          data: partitionCategory.map((p) => p.percentage),
          backgroundColor: partitionCategory.map((p) => p.color),
        },
      ],
    };
  }, [partitionCategory]);

  // Memoized filtered categories
  const farmBasedCategories = useMemo(
    () => categories.filter((cat) => cat.title.toLowerCase().includes("farm")),
    [categories]
  );
  const nonFarmCategories = useMemo(
    () => categories.filter((cat) => cat.title.toLowerCase().includes("non-farm")),
    [categories]
  );
  const climateCategories = useMemo(
    () => categories.filter((cat) => cat.title.toLowerCase().includes("climate")),
    [categories]
  );

  // Render category list
  const renderCategory = (cat: Category) => (
    <div key={cat._id} className="flex flex-col gap-2">
      <h3 className="font-lato-bold text-[#00897B]">{cat.title}</h3>
      <ul className="list-inside ml-4 list-disc text-gray-700">
        {cat.items.map((item, idx) => (
          <li
            key={idx}
            className="text-black text-sm lg:text-lg font-lato-regular py-1 md:py-1"
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );

  if (!chartData) return null;

  return (
    <section className="mt-6 lg:blade-top-margin-lg   blade-bottom-padding-lg">
      <div className="gsap-fade-in">
          <h2 className="2xl:text-4xl  sm:ps-5 text-[#07393C] font-lato-bold pb-2 ">
             {chartTitle}
          </h2>
           <hr className="border-1 border-[#84848480] lg:block hidden lg:w-[50%]" />

          
        <div className="flex flex-col sm:flex-row items-center gap-6 md:gap-10 blade-top-margin-sm justify-center">
          {/* Desktop Farm-Based */}
          <div className="hidden sm:flex flex-col gap-6">
            {farmBasedCategories.map(renderCategory)}
          </div>

          {/* Doughnut chart */}
          <div className="w-[18rem] h-[18rem] md:w-[28rem] md:h-[28rem]">
            <Doughnut data={chartData} />
          </div>

          {/* Mobile Farm-Based */}
          <div className="flex flex-col gap-2 sm:hidden">
            {farmBasedCategories.map(renderCategory)}
          </div>

          {/* Non-Farm & Climate */}
          <div className="flex flex-col gap-6">
           {/* {nonFarmCategories.map(renderCategory)}*/}
            {climateCategories.map(renderCategory)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChartGraph;
